<template>
  <div
    :id="'field-' + name"
    :data-test-policy-field="'field-' + name"
    :class="['row field-line', { 'push-up': modelValueData.ControlType === 'slider' }]"
  >
    <div class="columns">
      <LabelAndTooltip :value="modelValueData" />
    </div>
    <div class="columns" data-test-policy-field-value data-tag-value>
      <!-- static readonly singleline text -->
      <p v-if="!modelValueData.IsEditable && !editMode" v-html="labelName" />

      <!-- edit mode -static readonly singleline text -->
      <p
        v-if="
          modelValueData.ControlType === 'readonly-text' && !modelValueData.IsEditable && editMode
        "
        v-html="labelName"
      />

      <!-- edit mode - disabled button-switch - nej -->
      <span v-if="!modelValueData.IsEditable && editMode" class="policy-disabled-button-switch">
        <button-switch
          v-if="modelValueData.ControlType === 'disabled-switch'"
          :disabled="true"
          :name="name + '-disabled-switch'"
        />
      </span>

      <!-- text with edit icon -->
      <p
        v-if="editMode && modelValueData.IsEditable && !defaultToActiveField"
        class="editable"
        @click="editItem"
        v-html="labelName"
      />

      <!-- edit mode -->
      <span
        v-if="editMode && modelValueData.IsEditable && defaultToActiveField"
        class="policy-editable-field"
      >
        <Field
          v-slot="{ field }"
          v-if="modelValueData.ControlType === 'text-field'"
          v-model="modelValueData.Value"
          :rules="'required'"
          :label="labelName"
          :name="name"
        >
          <text-input
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
            :disabled="isDisabled"
          />
        </Field>

        <Field
          v-slot="{ field }"
          v-if="modelValueData.ControlType === 'drop-down'"
          v-model="modelValueData.Value"
          @update:modelValue="calculatePrice"
          :name="name"
        >
          <dropdown
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
            :options="modelValueData.Options"
          />
        </Field>

        <Field
          v-if="modelValueData.ControlType === 'slider'"
          v-model="modelValueData.Value"
          :name="name"
        >
          <slider
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
            :options="modelValueData.Options"
          />
        </Field>

        <Field
          v-slot="{ field }"
          v-if="modelValueData.ControlType === 'radio-group'"
          v-model="modelValueData.Value"
          :name="name"
        >
          <radio-group
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
            :options="modelValueData.Options"
          />
        </Field>

        <Field
          v-slot="{ field }"
          v-if="modelValueData.ControlType === 'switch'"
          v-model="modelValueData.Value"
          :name="name"
        >
          <button-switch
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
            :options="modelValueData.Options"
          />
        </Field>

        <Field
          v-slot="{ field }"
          v-if="modelValueData.ControlType === 'address'"
          v-model="modelValueData.Value"
          :name="name"
        >
          <address-search
            :name="field.name"
            :model-value="field.value"
            @update:modelValue="
              modelValueData.Value = $event;
              calculatePrice();
            "
          />
        </Field>
        <ValidationError :name="name" />
      </span>
    </div>
  </div>
</template>

<script>
import { clone, isEqual } from 'lodash';
import AddressSearch from './Form/Address.vue';
import ButtonSwitch from './Form/Switch.vue';
import Dropdown from './Form/Dropdown.vue';

import RadioGroup from './Form/RadioGroup.vue';
import Slider from './Form/Slider.vue';
import TextInput from './Form/TextInput.vue';
import './Form/StaticText.vue'; // TODO: is this needed?
import ValidationError from '@/components/UI/ValidationError.vue';
import { FormContextKey } from 'vee-validate';
import { inject } from 'vue';

export default {
  name: 'LBField',
  components: {
    TextInput,
    Dropdown,
    RadioGroup,
    ButtonSwitch,
    Slider,
    AddressSearch,
    ValidationError
  },
  emits: ['update:modelValue'],
  setup() {
    return { form: inject(FormContextKey, undefined) };
  },
  props: {
    modelValue: {
      type: Object,
      default() {
        return null;
      }
    },
    name: {
      type: String,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      defaultToActiveField: this.$store.state.policy.defaultToActiveField,
      initialValue: clone(this.modelValue.Value),
      editedValue: clone(this.modelValue.Value),
      modelValueData: this.modelValue
    };
  },
  computed: {
    editMode() {
      return this.$store.state.policy.editMode;
    },
    loading() {
      return this.$store.state.policy.loading;
    },
    labelName() {
      if (this.modelValueData === null) {
        return '';
      }

      if (this.modelValueData.Value.Text !== undefined) {
        return this.modelValueData.Value.Text;
      }

      // This is a multiline readonly field
      if (typeof this.modelValueData.Value === 'object' && this.modelValueData.Value.length > 0) {
        return this.arrayReadonlyValue(this.modelValueData.Value);
      }

      // This is a singleline readonly field:
      return this.modelValueData.Value;
    },
    initialValueName() {
      return this.getPropText(this.initialValue);
    },
    isDisabled() {
      return (
        (this.modelValueData.Value === this.initialValue &&
          this.editedValue === this.initialValue) ||
        this.modelValueData.Value === this.editedValue
      );
    },
    changedLabel() {
      const value = this.modelValueData.Value.Value;

      if (value === undefined) {
        return this.modelValueData.Value;
      }

      if (typeof value === 'boolean') {
        return value ? 'tilvalgt' : 'fravalgt';
      }

      return this.modelValueData.Value.Text;
    },
    unchangedValueInSelection() {
      return isEqual(this.modelValueData.Value, this.editedValue);
    }
  },
  watch: {
    loading() {
      if (!this.loading) {
        // uncomment to end edit mode after calculating
        // this.defaultToActiveField = this.loading;
      }
    },
    '$store.state.policy.defaultToActiveField': function (val) {
      this.defaultToActiveField = val;
    },
    modelValue() {
      this.modelValueData = this.modelValue;
      this.editedValue = this.modelValueData.Value;
    }
  },
  methods: {
    arrayReadonlyValue(val) {
      let str = '';
      let i;

      for (i in val) {
        str += '<span>' + val[i] + '</span><br />';
      }

      return str;
    },
    getPropText(prop) {
      if (prop === null) {
        return '';
      }

      if (prop.Text !== undefined) {
        return prop.Text;
      }

      return prop;
    },
    editItem() {
      this.defaultToActiveField = true;
    },
    calculatePrice(e) {
      if (this.unchangedValueInSelection) {
        return;
      }

      this.form.validate().then((result) => {
        if (!result) {
          return;
        }

        return this.getPrice();
      });
    },
    getPrice() {
      this.$emit('update:modelValue', this.modelValueData);

      let templateModel = this.modelValueData.TemplateModel;

      if (this.modelValueData.Value.Value.TemplateModel !== undefined) {
        templateModel = this.modelValueData.Value.Value.TemplateModel;
      }

      const endpoint = templateModel.ApiEndpoint;

      if (templateModel.SelectedAmount !== undefined) {
        templateModel.SelectedAmount = this.modelValueData.Value.Value;
      }

      this.editedValue = this.modelValueData.Value;

      this.$store.dispatch('policy/calculateNewPrice', {
        endpoint,
        templateModel
      });

      this.$store.dispatch('Events/register', {
        event: 'policy-view-cover-change',
        id: 'policyView',
        vm: {
          PolicyName: this.$store.getters['policy/getPolicyName'],
          cover: this.modelValueData.Label,
          option: this.modelValueData.Value.Text
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'node_modules/foundation-sites/scss/util/util';

.info-box {
  margin-top: rem-calc(-15);

  &::before {
    margin-left: 53.5%;
  }
}
</style>
